import { useState, useEffect, useMemo, useRef, lazy, Suspense, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { cls } from '@/ui/cls'
import OutcomeSheet from '@/features/leads/workflow/OutcomeSheet'
import LeadOverlay, { LeadOverlayLoading } from '@/features/leads/detail/LeadOverlay'
import { AnimatePresence } from 'motion/react'
import TimelineTab from '@/features/leads/detail/TimelineTab'
import CallbackToast from '@/features/leads/workflow/CallbackToast'
import FocusView from '@/features/leads/focus/FocusView'
import TableView from '@/features/leads/table/TableView'
import DoneState from '@/features/leads/workflow/DoneState'
import { ViewLoading } from '@/shared/Skeletons'
import EmptyState from '@/ui/EmptyState'
import ErrorBoundary from '@/ui/ErrorBoundary'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useIsMobile } from '@/hooks/use-mobile'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import GetMoreLeads from '@/features/leads/workflow/GetMoreLeads'
import AddLeadModal from '@/features/leads/workflow/AddLeadModal'
import Topbar from './Topbar'
import { usePresenceHeartbeat } from './usePresenceHeartbeat'
import { useFocusPrefs } from '@/features/leads/focus/useFocusPrefs'
import { useCurrentView } from './useCurrentView'
import { WriteAccessProvider, useWriteAccess } from '@/shared/write-access'
import { useViewTracking } from './useViewTracking'
import { useLeadTracking } from '@/features/leads/workflow/useLeadTracking'
import { useCallbackPoller } from '@/features/leads/workflow/useCallbackPoller'
// One lazy boundary per view. Map pulls in Leaflet, Admin is the largest
// feature in the app and most colleagues never open it, and Dashboard brings
// its own chart code -- none of it belongs in the chunk a seller downloads to
// start calling.
const MapView = lazy(() => import('@/features/map/MapView'))
const Dashboard = lazy(() => import('@/features/dashboard/Dashboard'))
const AdminPage = lazy(() => import('@/features/admin/AdminPage'))
const ManagePage = lazy(() => import('@/features/lead-management/ManagePage'))
import { OUTCOMES, type OutcomeKind } from '@/shared/domain'
import type { LeadRow } from '@/shared/types'
import { trackEvent } from '@/shared/api/telemetry'
import { queueNavDirection, type KeyTrigger } from '@/shared/api/trackMeta'
import { SAVE_OUTCOME_KEY, TOP_UP_KEY, useClaimNext, useLead, usePool, usePrefetchLead, useTopUp } from '@/shared/api/leads'
import { REFILL_DEBOUNCE_MS, REFILL_INTERVAL_MS, debounce } from '@/features/leads/workflow/refill'
import { isBlockingError } from '@/features/leads/workflow/focusLoad'
import { canOpenManage, canWorkQueue, hasPerm, useColleagues, useMe } from '@/shared/api/session'
import { QUEUE_PARAM, TEST_QUEUE } from '@/shared/routes'
import { resolveQueueParam } from '@/features/leads/workflow/queueParam'
import { useLeadsData } from '@/features/leads/workflow/useLeadsData'
import { useLeadDetail } from '@/features/leads/detail/useLeadDetail'
import { useLeadSelection } from '@/features/leads/workflow/useLeadSelection'
import { usePostponeLead } from '@/features/leads/workflow/useLeadMutations'
import { useOutcomeFlow } from '@/features/leads/workflow/useOutcomeFlow'
import { soonToday } from '@/features/leads/workflow/soonToday'
import { blocksOutcomeShortcut } from '@/features/leads/workflow/doNotContactRules'
import { useBrand } from '@/shared/white-label/useBrand'
import { maskPerson } from '@/shared/white-label/anonymise'

/**
 * Owns `viewAs` and nothing else, so WriteAccessProvider can sit above every
 * caller of useWriteAccess -- including the banner below, which both sets the
 * queue being viewed and reads the resulting access.
 *
 * `viewAs` lives in the URL (?queue=) so a reload stays on the seller's queue.
 */
export default function App() {
  const [params, setParams] = useSearchParams()
  const { data: me } = useMe()
  const { data: colleagues } = useColleagues()
  const queue = resolveQueueParam(params.get(QUEUE_PARAM), {
    me: me?.email,
    canViewOthers: me ? hasPerm(me, 'manage_users') : undefined,
    colleagues: colleagues?.users,
  })
  // Admin only: whose Focus queue is on screen. null = your own.
  const viewAs = queue.kind === 'seller' ? queue.email : null
  // Admin only: the test queue of QA test leads instead (YCRM-93).
  const testQueue = queue.kind === 'test'

  // A value that names no seller this viewer may see is cleared, not shown as
  // an empty queue. Re-checked on every URL change, so another write to the
  // search string in the same tick cannot leave it behind.
  const dropQueue = queue.kind === 'own' && queue.drop
  useEffect(() => {
    if (dropQueue) setParams(prev => withQueue(prev, null), { replace: true })
  }, [dropQueue, params, setParams])

  const setViewAs = useCallback((email: string | null) => {
    setParams(prev => withQueue(prev, email), { replace: true })
  }, [setParams])

  return (
    <WriteAccessProvider queueOwner={viewAs}>
      <Shell viewAs={viewAs} testQueue={testQueue} queuePending={queue.kind === 'pending'} setViewAs={setViewAs} />
    </WriteAccessProvider>
  )
}

function withQueue(prev: URLSearchParams, email: string | null): URLSearchParams {
  const next = new URLSearchParams(prev)
  if (email) next.set(QUEUE_PARAM, email)
  else next.delete(QUEUE_PARAM)
  return next
}

function Shell({ viewAs, testQueue, queuePending, setViewAs }: {
  viewAs: string | null
  testQueue: boolean
  /** The URL names a queue that cannot be checked yet; hold the Focus fetch. */
  queuePending: boolean
  setViewAs: (v: string | null) => void
}) {
  usePresenceHeartbeat()
  const qc = useQueryClient()
  const { view: mode, setView: setMode, adminDest, setAdminDest, leadsView,
          overlayLeadId, setOverlayLeadId } = useCurrentView()
  const { brand } = useBrand()

  // index.html ships the YLLW title, so the tab keeps saying YLLW until this
  // runs. Worth doing: a screenshot of a re-skinned CRM with the wrong name in
  // the browser tab defeats the point of the mode.
  useEffect(() => {
    document.title = brand.documentTitle
  }, [brand])
  useViewTracking(mode)
  const { focusContactInfo, setFocusContactInfo, focusSort, setFocusSort } = useFocusPrefs()

  // Opt-in: render the whole book — waiting, postponed and closed included — for
  // the seller who wants to see everything Table shows them.
  const [showAll, setShowAll] = useState(false)

  const { data: colleagues } = useColleagues()
  // Stable identity. An inline arrow here was a new function on every render of
  // this component, so `openOverlay` in TableView — which depends on it — was new
  // too, and every rendered row failed React.memo. Exactly the leak the
  // memoisation in TableView exists to prevent.
  const openOverlayById = useCallback((id: string) => setOverlayLeadId(id), [setOverlayLeadId])

  const [addLeadOpen, setAddLeadOpen] = useState(false)
  // Notes panel opens fresh on every load — closing it is a per-session
  // dismissal, not a persistent preference. Reload brings it back so the
  // BDR's running scratchpad is always visible at the start of a session.
  // On phones the panel would cover most of the screen, so it starts closed
  // below the breakpoint where Focus renders it as a side column (1200px —
  // below that the outcome bar's longer labels need the width back) and the
  // BDR opens it deliberately.
  const [notesOpen, setNotesOpen] = useState<boolean>(
    () => typeof window === 'undefined' || window.innerWidth >= 1200,
  )
  // Below 1200px the notes panel is a Sheet drawer rather than the grid
  // column below. Sheet portals its content to document.body, so a
  // min-[1200px]:hidden wrapper around it can't stop that drawer from
  // rendering (and dimming the page) at wide widths -- it has to be gated
  // in JS instead.
  const notesPanelIsDrawer = useIsMobile(1200)

  const {
    leads,
    list,
    bandCounts,
    focusContactCounts,
    me,
    leadsLoading,
    leadsPartial,
    leadsError,
    leadsData,
    reminderLeads,
  } = useLeadsData({ mode, focusContactInfo, focusSort, showAll, viewAs, testQueue, queuePending })
  // A failed reload keeps a loaded queue on screen; only an empty cache blocks.
  const blockingError = isBlockingError(leadsError, leads.length)
  // Översikt, Karta, admin and manage load no lead list, so an outcome started
  // in the overlay there targets the overlay's own card, the one it fetched.
  const { data: overlayCard } = useLead(!leadsView ? overlayLeadId : null)

  const {
    setSelectedId,
    selected,
    idx,
    nav,
    tab,
    setTab,
    advance,
    navigate,
    jumpToLead,
    resetToFirst,
  } = useLeadSelection({
    list,
    // Not on Tabell's first page: the lead open in Focus is rarely among its
    // rows, and the selection would fall back to the first of them.
    leadsData: leadsPartial ? undefined : leadsData,
    leads,
    inUrl: mode === 'focus',
    fallback: overlayCard,
  })

  // Records which lead is open and for how long. Focus previously emitted no
  // lead-scoped event at all, so the view where BDRs spend their day was a blind
  // spot in the analytics — and no duration was ever measured per lead, only per
  // view. Only tracks in Focus: in Table/Map the "open" is the row click, which
  // already emits lead_opened, and there is no single lead being dwelt on.
  useLeadTracking(mode === 'focus' ? (selected?.id ?? null) : null, selected?.stage)

  // The outcome flow calls useLeadWrites() itself, where its write happens;
  // Focus, Table, the overlay and the cards inside them do the same for
  // theirs. Nothing here for the shell to hold onto.
  // Postponing from the lead overlay must not also advance the FOCUS queue —
  // the overlay can show a lead that has nothing to do with whatever is
  // currently selected in Focus underneath it.
  const postponeLead = usePostponeLead(() => { if (!overlayLeadId) advance() })

  // The list carries list rows (no notes/timeline/AI brief). Panels
  // rendered here (the activity panel, OutcomeSheet) get the detail-merged lead;
  // FocusView and LeadOverlay merge their own.
  const selectedFull = useLeadDetail(selected)

  // Warm the detail cache for the next lead in the focus queue so advancing
  // never waits on the full-card fetch. Keyed on the id: `list` is a new array
  // on every 30 s clock tick and every poll, and re-running on those refetched
  // the next lead once a minute whenever its cached copy had gone stale.
  const prefetchLead = usePrefetchLead()
  const nextId = mode === 'focus' ? list[idx + 1]?.id : undefined
  useEffect(() => {
    if (nextId) prefetchLead(nextId)
  }, [nextId, prefetchLead])

  const { outcome, setOutcome, startOutcome, submitOutcome, closeOutcome } = useOutcomeFlow({
    selected,
    me,
    overlayLeadId,
    advance,
  })

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      const inField = !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')
      const canSubmit = outcome
        ? (!OUTCOMES[outcome.kind].askRating || outcome.rating > 0)
        : false

      if (outcome) {
        // Escape always closes the sheet, even mid-comment — preventDefault
        // so LeadOverlay's own Escape handler (if the sheet was opened from
        // there) knows this keystroke was already spent and doesn't also
        // close the overlay underneath it.
        if (e.key === 'Escape') { e.preventDefault(); closeOutcome({ source: 'keyboard', key: 'Escape' }); return }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && canSubmit) {
          e.preventDefault()
          submitOutcome({ source: 'keyboard', key: 'Cmd+Enter' })
          return
        }
        // Rating only while the comment is empty. The textarea has
        // autoFocus, so without the empty check, opening Booked or Not a
        // fit and immediately pressing 1-5 would type a digit into the
        // comment instead of rating — the field is focused before the BDR
        // has touched anything.
        if (OUTCOMES[outcome.kind].askRating && e.key >= '1' && e.key <= '5' && !outcome.comment.trim()) {
          e.preventDefault()
          setOutcome(o => (o ? { ...o, rating: parseInt(e.key) } : o))
          return
        }
        if (inField) return
        if (e.key === 'Enter' && canSubmit) submitOutcome({ source: 'keyboard', key: 'Enter' })
        return
      }
      // The overlay has its own keyboard handler, scoped to whichever lead
      // it's actually showing — which may not be `selected` at all (it can
      // be opened on an unrelated lead from Table/Dashboard/Map while Focus
      // keeps its own selection underneath).
      if (overlayLeadId) return
      if (mode !== 'focus' || !selected) return
      if (inField) return
      // Don't hijack browser shortcuts — Cmd+C / Ctrl+C should copy the
      // selected text, not open the "Already customer" outcome dialog.
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      // The next/previous buttons' tooltips have always named → and ← as
      // alternatives to ↓ and ↑ (step forward / back) — queueNavDirection is
      // what makes that true, rather than just j/k and ↓/↑.
      const navDir = queueNavDirection(e.key)
      const handled = ['r', 'n', 'b', 'x', 'c', 'w', 'e', 'p'].includes(k)
        || navDir !== null || e.key === 'Home'
      // A lead marked Kontakta ej takes no outcome and no postpone; moving
      // through the queue still works.
      if (!handled || blocksOutcomeShortcut(selected, k)) return
      // Without this, the same keypress that opens the outcome sheet can
      // also land in its auto-focused comment field: the sheet mounts and
      // steals focus before the browser gets to the default
      // character-insertion step for this event, which then applies to
      // whatever now has focus instead of doing nothing.
      e.preventDefault()
      // Named consistently with the (now-hidden) key chips the outcome bar
      // used to show, so the admin usage screen reads "R", "ArrowDown", etc.
      const via: KeyTrigger = {
        source: 'keyboard',
        key: e.key.startsWith('Arrow') || e.key === 'Home' ? e.key : k.toUpperCase(),
      }
      if (k === 'r') startOutcome('reached', '', via)
      else if (k === 'n') startOutcome('voicemail', '', via)
      else if (k === 'b') startOutcome('booked', '', via)
      else if (k === 'x') startOutcome('notfit', '', via)
      else if (k === 'c') startOutcome('customer', '', via)
      else if (k === 'w') startOutcome('bad_contact', '', via)
      else if (k === 'e') startOutcome('email_sent', '', via)
      // P → Postpone with a 1-week default. Power-users keep flying; if they
      // want a different duration, the button popover is one click away.
      else if (k === 'p') postponeLead(selected.id, 7)
      else if (navDir) navigate(navDir, via)
      else if (e.key === 'Home') resetToFirst(via)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, list, mode, outcome, overlayLeadId])

  const { callbackToast, setCallbackToast } = useCallbackPoller(reminderLeads, me?.email)

  // For the done screen: callbacks still promised later today, over the
  // seller's whole book (not just `list`, which is empty by the time this
  // screen shows — the done screen is reached precisely when `list` has
  // nothing left to work on right now).
  const dueToday = useMemo(() => soonToday(leads), [leads])

  const canAddLead = hasPerm(me, 'edit_contacts')
  const openAddLead = canAddLead ? () => { trackEvent('add_lead_opened'); setAddLeadOpen(true) } : undefined

  // Taking leads from the shared pool. Focus is strictly personal now, so this
  // (or claiming a specific lead from the Table) is the only way work reaches a
  // seller. Gated on the same permission /claim-next enforces, so the button is
  // never offered to someone who would only get a 403 from it.
  const canClaim = canWorkQueue(me)
  // Non-sellers browse read-only until they deliberately turn editing on — the
  // table commits on single clicks and an admin looking something up should not
  // be able to change a seller's pipeline by misclicking a row.
  // Server-gated on manage_users as well; this only decides whether to offer it.
  const canViewOthers = hasPerm(me, 'manage_users')
  const { canEdit, canToggle, setEditing } = useWriteAccess()
  const claimNext = useClaimNext()
  // Only asked for while the queue is empty — that is the one screen that needs
  // the number, and the pool is shared so the value goes stale quickly.
  const { data: pool } = usePool(canClaim && list.length === 0)
  const onTake = canClaim
    ? (count: number) => {
        claimNext.mutate(count, {
          onSuccess: res => {
            // Jump straight to the first lead taken so the seller can start
            // calling without another click.
            if (res.leads.length > 0) setSelectedId(res.leads[0].id)
          },
        })
      }
    : undefined

  // ── Top up the queue ────────────────────────────────────────────────────
  // How the leads get split: each seller is brought up to the same target from
  // the shared pool when they open Focus. The pull is atomic, so two sellers
  // arriving at once take disjoint sets, and nobody has to hand batches out.
  // Fires once per mount rather than on every render — it is idempotent, but
  // repeating it would spend requests for nothing.
  const topUp = useTopUp()
  const toppedUpRef = useRef(false)
  useEffect(() => {
    if (!canClaim || toppedUpRef.current || leadsLoading) return
    toppedUpRef.current = true
    topUp.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canClaim, leadsLoading])

  // Refill while selling in Focus, not only on open: a seller working through
  // their queue would otherwise run dry mid-session. A burst of outcome saves
  // sends one request, two seconds after the last, and none while a top-up is
  // still in flight. The pulled leads reach the list through useTopUp's own
  // invalidation, and no toast: a routine refill is not news, the empty state
  // keeps its GetMoreLeads messages.
  const topUpMutate = topUp.mutate
  useEffect(() => {
    if (!canClaim || mode !== 'focus') return
    const refill = debounce(() => {
      if (qc.isMutating({ mutationKey: [TOP_UP_KEY] }) === 0) topUpMutate({ silent: true })
    }, REFILL_DEBOUNCE_MS)
    const unsubscribe = qc.getMutationCache().subscribe(event => {
      if (event.type === 'updated' && event.action.type === 'success'
        && event.mutation.options.mutationKey?.[0] === SAVE_OUTCOME_KEY) refill.call()
    })
    return () => {
      unsubscribe()
      refill.cancel()
    }
  }, [canClaim, mode, qc, topUpMutate])

  // And for the seller who sits in Focus without saving. A tick that lands
  // while a write or a top-up is in flight is skipped, so the refetch it
  // triggers cannot replace an optimistic edit.
  useEffect(() => {
    if (!canClaim || mode !== 'focus') return
    const id = setInterval(() => {
      if (qc.isMutating({ predicate: m => m.options.mutationKey?.[0] !== 'presence' }) === 0) {
        topUpMutate({ silent: true })
      }
    }, REFILL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [canClaim, mode, qc, topUpMutate])

  // Toasts hang just below the banner and top bar: at the bottom they sat on
  // the outcome bar in Focus and the overlay (YCRM-96), and higher up they
  // would cover the view switch. Measured, because the banner comes and goes
  // with edit access.
  const headerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(56)
  useEffect(() => {
    const el = headerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setHeaderHeight(el.offsetHeight))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const toastOffset = { top: headerHeight + 8 }

  return (
    <div className="app" data-surface="fog" data-brand={brand.key}>
      <Toaster position="top-center" offset={toastOffset} mobileOffset={toastOffset} />
      <div className="layout">
        {/* Banner and topbar share the grid's first row — `.app` is
            `grid-template-rows: auto 1fr`, so a third child would steal the 1fr
            from the content area and leave a dead band under the header. */}
        <div ref={headerRef}>
        {canToggle && (
          <div
            className={cls(
              'flex items-center justify-center gap-3 px-4 py-1.5 text-xs border-b',
              canEdit
                ? 'bg-brand border-brand-deep text-ink-1000'
                : 'bg-ink-50 border-ink-200 text-ink-700',
            )}
          >
            {canViewOthers && (
              <label className="flex items-center gap-1.5">
                <span className="text-ink-500">Kö</span>
                <NativeSelect
                  size="sm"
                  className="h-auto py-0.5 text-xs"
                  value={testQueue ? TEST_QUEUE : viewAs ?? ''}
                  onChange={e => setViewAs(e.target.value || null)}
                >
                  <NativeSelectOption value="">Min</NativeSelectOption>
                  <NativeSelectOption value={TEST_QUEUE}>Testkö</NativeSelectOption>
                  {(colleagues?.users ?? [])
                    .filter(c => c.takesLeads)
                    .map(c => (
                      <NativeSelectOption key={c.email} value={c.email}>
                        {brand.key === 'yllw' ? (c.name || c.email) : maskPerson(c.email)}
                      </NativeSelectOption>
                    ))}
                </NativeSelect>
              </label>
            )}
            {!canEdit ? (
              <>
                <span>
                  {testQueue
                    ? 'Visar testkön, alla testleads oavsett ägare. Skrivskyddat.'
                    : viewAs
                    ? `Visar ${viewAs}s kö — skrivskyddat, inget du klickar på här ändrar den.`
                    : 'Skrivskyddat — du kan titta på leads men inte ändra dem.'}
                </span>
                <Button variant="link" className="text-xs" onClick={() => setEditing(true)}>
                  Aktivera redigering
                </Button>
              </>
            ) : (
              <>
                <span className="font-medium">
                  {testQueue
                    ? 'Redigerar testkön. Utfall du loggar här påverkar bara testleads.'
                    : viewAs
                    ? `Redigerar ${viewAs}s kö — ändringar påverkar deras skarpa leads.`
                    : "Redigering aktiverad — ändringar du gör påverkar säljarnas skarpa leads."}
                </span>
                <Button variant="link" className="text-xs" onClick={() => setEditing(false)}>
                  Tillbaka till skrivskyddat
                </Button>
              </>
            )}
          </div>
        )}
        {/* TOPBAR */}
        <Topbar
          mode={mode}
          setMode={setMode}
          notesOpen={notesOpen}
          setNotesOpen={setNotesOpen}
          me={me}
          adminDest={adminDest}
          setAdminDest={setAdminDest}
          onAddLead={openAddLead}
          onOpenLead={id => setOverlayLeadId(id)}
        />
        </div>

        {/* MAIN + optional notes side panel.
            Panel only renders when open, a lead is showing, and we're not
            on Dashboard. The overlay (if open) covers this row entirely
            and provides its own internal notes layout.

            Wrapped in one error boundary: Focus, Table, Dashboard, Admin and
            the lead overlay all render from here down, and a render error in
            any of them used to blank the whole app rather than just that
            view. */}
        <ErrorBoundary>
        <main className={cls(
          'bg-background overflow-hidden min-h-0 grid grid-cols-1',
          // 1200px, not the usual lg (1024px) — below that, the outcome
          // bar's longer "what happens next" labels need the width the
          // side panel would otherwise take.
          notesOpen && selected && mode === 'focus' && !overlayLeadId
            ? 'min-[1200px]:grid-cols-[1fr_320px]'
            : 'min-[1200px]:grid-cols-1',
        )}>
        <div className="min-h-0 min-w-0 overflow-hidden flex flex-col">
          {/* Dashboard short-circuits the leads-query gating below so it
              renders even on an empty CRM (it has its own loading/empty
              states). */}
          {mode === 'dashboard' && (
            <Suspense fallback={<ViewLoading label="Laddar översikten" />}>
              <Dashboard onSelectLead={id => setOverlayLeadId(id)} />
            </Suspense>
          )}
          {mode === 'map' && (
            <Suspense fallback={<ViewLoading label="Laddar kartan" />}>
              <MapView onSelectLead={id => setOverlayLeadId(id)} />
            </Suspense>
          )}
          {mode === 'admin' && (
            hasPerm(me, 'manage_users')
              ? (
                <Suspense fallback={<ViewLoading />}>
                  <AdminPage me={me!} dest={adminDest} onOpenLead={id => setOverlayLeadId(id)} />
                </Suspense>
              )
              : <EmptyState title="Endast för admin" body="Du har inte åtkomst till den här sidan." />
          )}
          {mode === 'manage' && (
            canOpenManage(me)
              ? (
                <Suspense fallback={<ViewLoading />}>
                  <ManagePage />
                </Suspense>
              )
              : <EmptyState title="Ingen åtkomst" body="Du har inte behörighet att hantera leads." />
          )}
          {leadsView && blockingError && (
            <EmptyState
              title="Kunde inte ladda leads"
              body={(leadsError as Error).message || 'Nätverksfel — försök ladda om sidan.'}
            />
          )}
          {/* No data yet rather than isLoading: a write cancels the first load
              when it lands mid-flight, and v5 then reports not-loading with no
              data, which would read as an empty queue. */}
          {leadsView && !blockingError && leadsData === undefined && (
            <ViewLoading label={mode === 'table' ? 'Laddar leads' : 'Laddar lead'} />
          )}
          {/* An empty response means two completely different things depending on
              the view, and getting it wrong strands the seller. Focus is
              per-owner, so empty there means "you own no leads" — the CRM may be
              full — and the answer is to take some. Only the Table, which shows
              the whole pipeline, can legitimately say the CRM is empty. */}
          {leadsView && !blockingError && leadsData !== undefined && leads.length === 0 && (
            mode === 'focus' ? (
              canClaim ? (
                <GetMoreLeads
                  variant="empty"
                  onSwitch={() => setMode('table')}
                  onClaimed={ids => setSelectedId(ids[0])}
                />
              ) : (
                <EmptyState
                  title="Inga leads tilldelade dig"
                  body="Focus visar bara leads du äger. Öppna listan för att se pipelinen."
                  actionLabel="Öppna listan"
                  onAction={() => setMode('table')}
                />
              )
            ) : (
              <EmptyState
                title="Inga leads i CRM:et än"
                body="Lägg till din första lead, eller skicka in dem från leadgen för att komma igång."
                actionLabel={openAddLead && canEdit ? 'Lägg till lead' : undefined}
                onAction={canEdit ? openAddLead : undefined}
              />
            )
          )}
          {leadsView && !blockingError && leads.length > 0 && (<>
            {leadsError && (
              <div
                role="status"
                className="shrink-0 mx-4 mt-3 rounded border border-ink-200 bg-ink-50 px-3 py-1.5 text-xs text-ink-700"
              >
                Kunde inte uppdatera listan. Försöker igen automatiskt.
              </div>
            )}
            {mode === 'focus' && (
              selected ? (
                <div className="flex-1 min-h-0">
                  <FocusView
                    lead={selected}
                    nav={nav}
                    listLength={list.length}
                    idx={idx}
                    tab={tab}
                    setTab={setTab}
                    list={list}
                    bandCounts={bandCounts}
                    showAll={showAll}
                    onShowAllChange={setShowAll}
                    contactInfo={focusContactInfo}
                    onContactInfoChange={setFocusContactInfo}
                    sort={focusSort}
                    onSortChange={setFocusSort}
                    contactCounts={focusContactCounts}
                    onPrev={() => navigate('back')}
                    onSkip={() => navigate('forward')}
                    onFirst={resetToFirst}
                    onJumpTo={jumpToLead}
                    onAction={startOutcome}
                    onPostpone={postponeLead}
                    onMarkedDoNotContact={advance}
                  />
                </div>
              ) : (
                <DoneState
                  onSwitch={() => setMode('table')}
                  onTake={onTake}
                  taking={claimNext.isPending}
                  available={pool?.available}
                  callbacksDueToday={dueToday.count}
                  nextCallbackAt={dueToday.nextAt}
                />
              )
            )}

            {mode === 'table' && (
              <TableView leads={list} partial={leadsPartial} onOverlay={openOverlayById} />
            )}
          </>)}
        </div>
        {selectedFull && mode === 'focus' && !overlayLeadId && (<>
          {/* 1200px+: notes live in their own grid column (today's behavior). */}
          <div className="hidden min-[1200px]:contents">
            {notesOpen && (
              <TimelineTab
                lead={selectedFull}
                onAction={startOutcome}
                panel
                onClose={() => setNotesOpen(false)}
              />
            )}
          </div>
          {/* below 1200px: slide-in drawer over the content, mirroring LeadOverlay.
              Rendered only when notesPanelIsDrawer is true -- see its declaration
              for why a CSS-only wrapper can't gate this. */}
          {notesPanelIsDrawer && (
            <Sheet open={notesOpen} onOpenChange={next => { if (!next) setNotesOpen(false) }}>
              <SheetContent side="right" size="sm" showCloseButton={false} className="w-[min(320px,90vw)] sm:max-w-none">
                <SheetTitle className="sr-only">Aktivitet</SheetTitle>
                <TimelineTab
                  lead={selectedFull}
                  onAction={startOutcome}
                  panel
                  onClose={() => setNotesOpen(false)}
                />
              </SheetContent>
            </Sheet>
          )}
        </>)}
        </main>

        <AnimatePresence>
        {outcome && selectedFull && (
          <OutcomeSheet
            lead={selectedFull}
            outcome={outcome}
            setOutcome={(next) => {
              setOutcome(o => (typeof next === 'function' ? next(o!) : next))
            }}
            onCancel={() => closeOutcome()}
            onEscapeKeyDown={() => closeOutcome({ source: 'keyboard', key: 'Escape' })}
            onSubmit={() => submitOutcome()}
          />
        )}
        </AnimatePresence>

        <AnimatePresence>
        {addLeadOpen && (
          <AddLeadModal
            onClose={() => setAddLeadOpen(false)}
            onCreated={(lead) => {
              setAddLeadOpen(false)
              // The create mutation has put the lead in ['lead', id] and the
              // table lists, so the overlay opens on it without a fetch.
              setOverlayLeadId(lead.id)
            }}
            onOpenExisting={(id) => {
              setAddLeadOpen(false)
              setOverlayLeadId(id)
            }}
          />
        )}
        </AnimatePresence>

        <AnimatePresence>
        {callbackToast && (
          <CallbackToast
            lead={callbackToast}
            onJump={() => {
              const id = callbackToast.id
              setCallbackToast(null)
              setMode('focus')
              jumpToLead(id)
            }}
            onDismiss={() => setCallbackToast(null)}
          />
        )}
        </AnimatePresence>

        <AnimatePresence>
        {overlayLeadId && (
          <OverlayLead
            id={overlayLeadId}
            fromList={leads.find(l => l.id === overlayLeadId) ?? null}
            onClose={() => setOverlayLeadId(null)}
            onAction={(id, kind, via) => {
              // Make the outcome target the overlay's lead, not the focus queue's
              setSelectedId(id)
              startOutcome(kind, '', via)
            }}
            notesOpen={notesOpen}
            onSetNotesOpen={setNotesOpen}
            outcomeOpen={!!outcome}
            onPostpone={postponeLead}
          />
        )}
        </AnimatePresence>
        </ErrorBoundary>
      </div>
    </div>
  )
}

/**
 * The lead overlay, addressed by id.
 *
 * `?lead=<id>` can name any company, not just one in the list this browser
 * happens to have loaded -- that is the point of it being in the URL. So the
 * list is only a fast path: miss it and the single-lead query fetches the
 * company on its own, which is what makes a link sent to a colleague work
 * even when the lead is not in their queue.
 */
function OverlayLead({ id, fromList, onClose, onAction, ...rest }: {
  id: string
  fromList: LeadRow | null
  onClose: () => void
  onAction: (id: string, kind: OutcomeKind, via?: KeyTrigger) => void
} & Omit<
  React.ComponentProps<typeof LeadOverlay>,
  'lead' | 'onClose' | 'onAction' | 'slideIn'
>) {
  const { data: fetched, isLoading, isError } = useLead(fromList ? null : id)
  const lead = fromList ?? fetched ?? null
  // Opened before the lead was here, so the loading panel is already in place.
  const [arrivedLate] = useState(() => lead === null)

  if (!lead) {
    if (isLoading) {
      return <LeadOverlayLoading onClose={onClose} notesOpen={rest.notesOpen} onSetNotesOpen={rest.onSetNotesOpen} />
    }
    // A link to something this account cannot see, or that no longer exists.
    // Silence would look like a broken button, so say so and close cleanly.
    return (
      <Dialog open onOpenChange={next => { if (!next) onClose() }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Leaden otillgänglig</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-muted-foreground">
              {isError
                ? 'Leaden kunde inte laddas — den kan ha tagits bort, eller så har ditt konto inte åtkomst till den.'
                : 'Leaden kunde inte hittas.'}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={onClose}>Stäng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <LeadOverlay
      lead={lead}
      onClose={onClose}
      onAction={(kind, _comment, via) => onAction(lead.id, kind, via)}
      slideIn={!arrivedLate}
      {...rest}
    />
  )
}
